const ROOT_SELECTOR = '[data-rsv-mega-root]';
const INIT_ATTR = 'data-rsv-mega-initialized';

function track(eventName, source) {
  if (!eventName || !source) return;

  const detail = {
    path: window.location.pathname,
  };

  for (const attr of source.attributes || []) {
    if (attr.name.startsWith('data-rsv-attr-')) {
      const key = attr.name.replace('data-rsv-attr-', '').replace(/-/g, '_');
      detail[key] = attr.value;
    }
  }

  const affinity = source.getAttribute('data-rsv-affinity');
  if (affinity && window.RSV_PROFILE?.affinity) {
    const parts = affinity.split(':');
    if (parts.length >= 3) {
      window.RSV_PROFILE.affinity(parts[0], parts[1], Number.parseFloat(parts[2]) || 0.1);
    } else if (parts.length === 2) {
      window.RSV_PROFILE.affinity(parts[0], parts[1], 0.1);
    }
  }

  if (window.RSV_PROFILE?.track) {
    window.RSV_PROFILE.track(eventName, detail);
  } else if (window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...detail });
  }
}

function activateMobileTab(root, tab) {
  const handle = tab.getAttribute('data-rsv-mega-tab');
  if (!handle) return;

  const tabs = root.querySelectorAll('[data-rsv-mega-tab]');
  const panels = root.querySelectorAll('[data-rsv-mega-panel-content]');

  tabs.forEach((candidate) => {
    const isActive = candidate === tab;
    candidate.classList.toggle('is-active', isActive);
    candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });

  panels.forEach((panel) => {
    const isActive = panel.getAttribute('data-rsv-mega-panel-content') === handle;
    panel.classList.toggle('is-active', isActive);
    panel.toggleAttribute('hidden', !isActive);
  });

  const tabScroller = tab.closest('.rsv-mega-drawer__tabs');
  if (tabScroller instanceof HTMLElement) {
    const targetLeft = tab.offsetLeft - (tabScroller.clientWidth - tab.clientWidth) / 2;
    tabScroller.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
  }
  track('mega_menu_opened', tab);
}

function updateMobileTabFade(tabScroller) {
  if (!(tabScroller instanceof HTMLElement)) return;

  const maxScroll = Math.max(0, tabScroller.scrollWidth - tabScroller.clientWidth);
  tabScroller.classList.toggle('has-left', tabScroller.scrollLeft > 4);
  tabScroller.classList.toggle('at-end', maxScroll - tabScroller.scrollLeft < 4);
}

function initMobileTabScroller(root) {
  const tabScroller = root.querySelector('.rsv-mega-drawer__tabs');
  if (!(tabScroller instanceof HTMLElement)) return;

  const update = () => updateMobileTabFade(tabScroller);
  tabScroller.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  requestAnimationFrame(update);
}

function focusWithoutScroll(element) {
  try {
    element.focus({ preventScroll: true });
  } catch (_error) {
    element.focus();
  }
}

function openDesktopPanel(root, item) {
  if (typeof root.activate === 'function') {
    root.activate({ target: item });
  } else {
    item.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
  }
}

function getDesktopPanelItem(root, source) {
  const item = source.closest('[data-rsv-mega-panel-handle]');
  return item instanceof HTMLElement && root.contains(item) ? item : null;
}

function getDesktopPanelTrigger(item) {
  const trigger = item.querySelector('[data-rsv-mega-panel]');
  return trigger instanceof HTMLElement ? trigger : null;
}

function getDesktopSubmenu(item) {
  const submenu = item.querySelector('[ref="submenu[]"], .menu-list__submenu');
  return submenu instanceof HTMLElement ? submenu : null;
}

function activateDesktopPanel(root, trigger) {
  const item = getDesktopPanelItem(root, trigger);
  if (!item) return null;

  focusWithoutScroll(trigger);
  openDesktopPanel(root, item);
  return item;
}

function initRoot(root) {
  if (!(root instanceof HTMLElement) || root.hasAttribute(INIT_ATTR)) return;
  root.setAttribute(INIT_ATTR, 'true');

  const openedPanels = new Set();
  const details = root.querySelector('#Details-menu-drawer-container');
  let clickOpenItem = null;
  let clickOpenRecoveryFrame = 0;

  initMobileTabScroller(root);

  function clearClickOpenItem() {
    if (clickOpenRecoveryFrame) {
      cancelAnimationFrame(clickOpenRecoveryFrame);
      clickOpenRecoveryFrame = 0;
    }
    clickOpenItem = null;
  }

  function clickOpenPanelNeedsRecovery(item) {
    const trigger = getDesktopPanelTrigger(item);
    const submenu = getDesktopSubmenu(item);

    return (
      root.getAttribute('aria-expanded') !== 'true' ||
      trigger?.getAttribute('aria-expanded') !== 'true' ||
      (submenu instanceof HTMLElement && !submenu.hasAttribute('data-active'))
    );
  }

  function scheduleClickOpenRecovery(target) {
    if (!clickOpenItem || !(target instanceof Node) || !clickOpenItem.contains(target)) return;
    if (clickOpenRecoveryFrame) return;

    clickOpenRecoveryFrame = requestAnimationFrame(() => {
      clickOpenRecoveryFrame = 0;
      if (!clickOpenItem || !root.contains(clickOpenItem)) {
        clickOpenItem = null;
        return;
      }
      if (clickOpenPanelNeedsRecovery(clickOpenItem)) {
        openDesktopPanel(root, clickOpenItem);
      }
    });
  }

  root.addEventListener('pointerover', (event) => {
    scheduleClickOpenRecovery(event.target);

    const item = event.target.closest?.('[data-rsv-mega-panel-handle]');
    if (!(item instanceof HTMLElement) || !root.contains(item)) return;

    const handle = item.getAttribute('data-rsv-mega-panel-handle');
    if (!handle || openedPanels.has(handle)) return;

    openedPanels.add(handle);
    track('mega_menu_opened', item);
  }, true);

  root.addEventListener('pointermove', (event) => {
    scheduleClickOpenRecovery(event.target);
  });

  root.addEventListener('pointerout', (event) => {
    if (!clickOpenItem || !(event.target instanceof Node) || !clickOpenItem.contains(event.target)) return;
    if (event.relatedTarget instanceof Node && clickOpenItem.contains(event.relatedTarget)) return;

    clearClickOpenItem();
  }, true);

  root.addEventListener('focusin', (event) => {
    const item = event.target.closest?.('[data-rsv-mega-panel-handle]');
    if (!(item instanceof HTMLElement) || !root.contains(item)) return;

    track('mega_menu_opened', item);
  });

  root.addEventListener('click', (event) => {
    const tab = event.target.closest?.('[data-rsv-mega-tab]');
    if (tab instanceof HTMLElement && root.contains(tab)) {
      clearClickOpenItem();
      activateMobileTab(root, tab);
      return;
    }

    const panelTrigger = event.target.closest?.('[data-rsv-mega-panel]');
    if (panelTrigger instanceof HTMLElement && root.contains(panelTrigger)) {
      event.preventDefault();
      clickOpenItem = activateDesktopPanel(root, panelTrigger);
      return;
    }

    const realLink = event.target.closest?.('a[href]');
    if (realLink instanceof HTMLAnchorElement && root.contains(realLink)) {
      clearClickOpenItem();
    }

    const tracked = event.target.closest?.('[data-rsv-mega-event]');
    if (tracked instanceof HTMLElement && root.contains(tracked)) {
      track(tracked.getAttribute('data-rsv-mega-event'), tracked);
    }
  });

  root.addEventListener('keydown', (event) => {
    const tab = event.target.closest?.('[data-rsv-mega-tab]');
    if (!(tab instanceof HTMLElement) || !root.contains(tab)) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    const tabs = [...root.querySelectorAll('[data-rsv-mega-tab]')].filter(
      (candidate) => candidate instanceof HTMLElement && !candidate.hidden
    );
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex < 0 || tabs.length < 2) return;

    event.preventDefault();
    let nextIndex = currentIndex;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;

    const nextTab = tabs[nextIndex];
    activateMobileTab(root, nextTab);
    focusWithoutScroll(nextTab);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    clearClickOpenItem();
    if (!details?.open) return;

    event.preventDefault();
    if (typeof root.close === 'function') {
      root.close();
    } else {
      details.classList.remove('menu-open');
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    }
  });
}

function init() {
  document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
